import './exchange-role.css'

/**
 * Microsoft's own steps for giving an application a directory role, at the section that does it.
 *
 * The page is written for someone registering their own app, so it is linked at Option 1 of step 5
 * rather than at the top, where it starts with certificates the customer has no use for. The anchor
 * was checked against the live page on 2026-09-27; if Microsoft renames the heading, the link still
 * opens the right page.
 */
export const EXCHANGE_ROLE_HELP_URL =
  'https://learn.microsoft.com/powershell/exchange/app-only-auth-powershell-v2' +
  '#option-1-assign-microsoft-entra-roles-to-the-application'

/**
 * THE ONE STEP CONSENT CANNOT DO: a directory role for Exchange.
 *
 * Exchange Online -- and Defender for Office 365, whose policies are read through Exchange --
 * authorises an application by the directory role it holds, not by the permission consent grants.
 * Exchange.ManageAsApp is on the consent screen and on its own lets Defenssive read nothing. Consent
 * cannot assign a role, so somebody in the customer's tenant has to, once. Until they do, every Email
 * check is refused by Microsoft and recorded as not assessed (ExchangeReader, UnreadReason.OfExchange).
 *
 * The Connect screen said nothing about this, while the published privacy notice said "our setup
 * asks for Global Reader or Security Reader". This is that setup, in the product. It is shown before
 * consent (the Connect screen) and after it (Settings, Microsoft tenant), because the role can only be
 * assigned once consent has created Defenssive's application in the tenant.
 *
 * GLOBAL READER IS RECOMMENDED (owner, 2026-09-27), because it was MEASURED. In the pilot tenant,
 * Get-ManagementRole -Cmdlet for every cmdlet on ExchangeReader's allowlist showed the Security Reader
 * role holding 7 of the 12 and View-Only Configuration (Global Reader) all 12. Security Reader lacks
 * Get-AcceptedDomain, Get-OrganizationConfig, Get-TransportConfig, Get-RemoteDomain and
 * Get-SharingPolicy, so under it these are refused and shown as not assessed: SPF, DKIM and the four
 * DMARC checks (all need the accepted-domain list), SMTP AUTH, automatic forwarding, calendar and
 * contact sharing, mailbox auditing, Reject Direct Send, and the part of the mail flow rule check that
 * needs the domain list. Security Reader is still offered, narrower, with that list said plainly
 * (docs/what-role-we-ask-a-customer-for.md has the measurement).
 *
 * Deliberately NOT said:
 *  - an Exchange role group as an alternative. AssessmentAccessReadOnlyCheck inspects directory roles
 *    only, and the privacy notice says setup does not ask for one.
 *  - how long the role takes to apply. Microsoft gives no figure.
 *
 * Both roles are marked privileged in Microsoft's role reference, and built-in directory roles apply
 * to the whole tenant; saying so first is better than a security team finding it out.
 */
export function ExchangeRoleSteps() {
  return (
    <div className="exchange-role">
      <ol className="exchange-role-steps">
        <li>
          Sign in to the Microsoft Entra admin center, <span className="mono">entra.microsoft.com</span>,
          as a Global Administrator or a Privileged Role Administrator, and open{' '}
          <strong>Roles &amp; admins</strong>.
        </li>
        <li>
          Select <strong>Global Reader</strong> by its name. We recommend it: it lets every Exchange
          Online check run. <strong>Security Reader</strong> is narrower, but Microsoft does not let
          it read the settings some checks need (listed below). Both are privileged roles that apply
          to your whole tenant.
        </li>
        <li>
          Choose <strong>Add assignments</strong>, search for <strong>Defenssive</strong>, and add the
          application, not a person. If Microsoft asks for an assignment type, choose{' '}
          <strong>Active</strong>: an application cannot activate a role the way a person does. An
          assignment with an end date stops the Exchange checks when it ends.
        </li>
      </ol>
      <p className="exchange-role-note">
        Without the role, Microsoft refuses Defenssive&rsquo;s Exchange reads. Under Security
        Reader, it also refuses the reads behind these checks: SPF, DKIM and DMARC (they need your
        list of domains), SMTP AUTH, automatic forwarding, calendar and contact sharing, mailbox
        auditing, Reject Direct Send, and part of the mail flow rule check. Checks Microsoft refuses
        are shown as not assessed, never as passing. A new
        role can take a while to take effect. Defenssive checks the directory roles its application
        holds, and reports a High finding against itself if any of them is not one of these two.{' '}
        <a href={EXCHANGE_ROLE_HELP_URL} target="_blank" rel="noopener noreferrer">
          Microsoft&rsquo;s instructions for assigning a role to an application
        </a>
      </p>
    </div>
  )
}
